// Browser-side sealing of an access secret (AES-GCM, WebCrypto).
//
// Today the content key travels with the sealed blob, so the server stores the
// key next to the ciphertext and the studio can technically read the value. This
// keeps the plaintext out of requests and logs; it does not hide the value from
// the studio or from anyone with a copy of the database.
// Hiding it from the server needs the key wrapped for the recipient
// (MK42-5821 step 2, an owner decision) - not done yet.

export type SealedSecret = { iv: string; ciphertext: string; key: string };

const b64 = (buf: ArrayBuffer) => btoa(String.fromCharCode(...new Uint8Array(buf)));
const unb64 = (s: string) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));

export async function encryptSecret(value: string): Promise<SealedSecret> {
  const key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, new TextEncoder().encode(value));
  const raw = await crypto.subtle.exportKey('raw', key);
  return { iv: b64(iv.buffer), ciphertext: b64(ciphertext), key: b64(raw) };
}

export async function decryptSecret(sealed: SealedSecret): Promise<string> {
  const key = await crypto.subtle.importKey('raw', unb64(sealed.key), 'AES-GCM', false, ['decrypt']);
  const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: unb64(sealed.iv) }, key, unb64(sealed.ciphertext));
  return new TextDecoder().decode(plain);
}
